import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Box, 
  Link,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import Logo from './Logo';
import BranchSelection from './BranchSelection';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [branchSelectionLoading, setBranchSelectionLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, branchSelection, setPreferredBranch } = useAuth();

  // Check for messages from navigation (e.g., from email verification)
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
    if (location.state?.warning) {
      setWarningMessage(location.state.warning);
    }
    // Clear the state to prevent message showing again on refresh
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowPasswordRecovery(false);
    try {
      const response = await login(formData);
      
      // If branch selection is not required, navigate to dashboard
      if (!response.requiresBranchSelection) {
        navigate('/');
      }
      // If branch selection is required, the BranchSelection modal will show
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'Login failed. Please check your credentials and try again.';
      setError(errorMessage);
      
      // Show password recovery option for failed login attempts
      if (errorMessage.includes('Invalid email or password') || errorMessage.includes('Login failed')) {
        setShowPasswordRecovery(true);
      }
    }
  };

  const handleBranchSelect = async (branchName) => {
    setBranchSelectionLoading(true);
    try {
      await setPreferredBranch(branchName);
      navigate('/');
    } catch (error) {
      console.error('Branch selection error:', error);
      setError(error.message || 'Failed to set preferred branch');
    } finally {
      setBranchSelectionLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Logo height="80px" />
            </Box>
            <Typography component="h1" variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
              Login
            </Typography>
            
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
            
            {warningMessage && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Vehicle Assignment Notice
                </Typography>
                {warningMessage}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Login
              </Button>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                  {showPasswordRecovery && (
                    <Box sx={{ mt: 1 }}>
                      <Link 
                        component={RouterLink} 
                        to="/forgot-password" 
                        variant="body2"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Forgot your password? Reset it here
                      </Link>
                    </Box>
                  )}
                </Alert>
              )}
              <Box sx={{ mt: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link component={RouterLink} to="/register" variant="body2">
                  {"Don't have an account? Register here"}
                </Link>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Forgot your password?
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      
      {/* Branch Selection Modal */}
      <BranchSelection
        open={branchSelection.required}
        branches={branchSelection.branches}
        user={branchSelection.tempUser}
        onBranchSelect={handleBranchSelect}
        loading={branchSelectionLoading}
      />
    </Container>
  );
};

export default LoginForm;